import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { getMyProfile, uploadImage } from "../../api";
import Dropdown from "../../components/Dropdown";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import PageLoader from "../../components/PageLoader";
import {
  ONLY_ALPHABETS,
  MOBILE_INDIA,
  PAN_INDIA,
  testRegex,
} from "../../config/regex";
import Swal from "sweetalert2";
import { colors } from "../../config/colors";

const UserProfile = () => {
  const navigate = useNavigate();
  const { user, updateUserProfile } = useAuth();

  const fileInputRef = useRef(null);

  const [fullName, setFullName] = useState(user?.name || "");
  const [mobile, setMobile] = useState(user?.mobile ?? user?.number ?? "");
  const [pan, setPan] = useState(user?.pan || "");
  const [experience, setExperience] = useState(user?.experience || "");
  const [risk, setRisk] = useState(user?.risk || "");
  const [broker, setBroker] = useState(user?.broker || "");
  const [profileImage, setProfileImage] = useState(user?.profileImage || "");
  const [joinedAt, setJoinedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const normalizeMobile = (value) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
  const normalizePan = (value) =>
    String(value ?? "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);

  const validateField = (field, value) => {
    const v = String(value ?? "").trim();
    switch (field) {
      case "fullName": {
        if (!v) return "Full name is required";
        if (!testRegex(ONLY_ALPHABETS, v))
          return "Name should contain letters and spaces only";
        if (v.length < 2) return "Name is too short";
        return "";
      }
      case "mobile": {
        if (!v) return "Mobile number is required";
        if (!testRegex(MOBILE_INDIA, v))
          return "Enter a valid Indian mobile number";
        return "";
      }
      case "pan": {
        if (!v) return "PAN number is required";
        const upper = v.toUpperCase();
        if (!testRegex(PAN_INDIA, upper))
          return "Enter a valid PAN (e.g. ABCDE1234F)";
        return "";
      }
      case "experience": {
        if (!v) return "Trading experience is required";
        const allowed = ["Beginner", "Intermediate", "Expert"];
        if (!allowed.includes(v)) return "Select a valid experience";
        return "";
      }
      case "risk": {
        if (!v) return "Risk appetite is required";
        const allowed = ["Low", "Medium", "High"];
        if (!allowed.includes(v)) return "Select a valid risk appetite";
        return "";
      }
      default:
        return "";
    }
  };

  const setAndValidate = (field, nextValue, setter) => {
    setter(nextValue);
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, nextValue),
    }));
  };

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      try {
        const profile = await getMyProfile();
        if (profile) {
          setFullName(profile.name || "");
          setMobile(profile.mobile ?? profile.number ?? "");
          setPan(profile.pan || "");
          setExperience(profile.experience || "");
          setRisk(profile.risk || "");
          setBroker(profile.broker || "");
          setProfileImage(profile.profileImage || "");
          if (profile.createdAt) {
            setJoinedAt(new Date(profile.createdAt).toLocaleDateString());
          }
        }
      } catch {
        setFullName(user.name || "");
        setMobile(user.mobile ?? user.number ?? "");
        setPan(user.pan || "");
        setExperience(user.experience || "");
        setRisk(user.risk || "");
        setBroker(user.broker || "");
        setProfileImage(user.profileImage || "");
        if (user.createdAt)
          setJoinedAt(new Date(user.createdAt).toLocaleDateString());
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      Swal.fire({
        title: "Uploading...",
        text: "Please wait while your image is being uploaded.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const { url } = await uploadImage(file);
      setProfileImage(url);
      Swal.fire({
        icon: "success",
        title: "Image Uploaded",
        text: 'Click "Save changes" to complete your profile update.',
        confirmButtonColor: colors.primary[500],
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Failed to upload image. Please try again.",
        confirmButtonColor: colors.primary[500],
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    const trimmed = fullName.trim();
    const nextErrors = {
      fullName: validateField("fullName", trimmed),
      mobile: validateField("mobile", mobile),
      pan: validateField("pan", pan),
      experience: validateField("experience", experience),
      risk: validateField("risk", risk),
    };
    Object.keys(nextErrors).forEach((k) => {
      if (!nextErrors[k]) delete nextErrors[k];
    });
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setMessage("Please fix the highlighted fields.");
      return;
    }

    setIsSaving(true);
    setMessage("");

    try {
      await updateUserProfile({
        name: trimmed,
        mobile: normalizeMobile(mobile),
        pan: normalizePan(pan),
        experience,
        risk,
        profileImage,
      });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Profile updated successfully.",
        confirmButtonColor: colors.primary[500],
      }).then(() => {
        window.location.href = "/";
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to update profile. Please try again.",
        confirmButtonColor: colors.primary[500],
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return <PageLoader />;
  }

  const initials =
    (user.name || user.email || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "U";

  const isChanged =
    fullName.trim() !== (user.name || "") ||
    mobile !== (user.mobile ?? user.number ?? "") ||
    pan !== (user.pan || "") ||
    experience !== (user.experience || "") ||
    risk !== (user.risk || "") ||
    profileImage !== (user.profileImage || "");

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="flex items-center gap-4">
        {/* Profile Image */}
        <div className="relative">
          {profileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              className="w-16 h-16 rounded-full object-cover shadow-md"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary-600 text-white flex items-center justify-center text-2xl font-semibold shadow-md">
              {initials}
            </div>
          )}

          {/* Edit Button */}
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className="absolute bottom-0 right-0 bg-white border rounded-full px-1 text-xs shadow hover:bg-gray-100"
          >
            ✎
          </button>

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-sm text-gray-600">
            Manage your personal and trading information.
          </p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          {/* Full Name */}
          <TextInput
            label="Full name"
            value={fullName}
            onChange={(e) =>
              setAndValidate("fullName", e.target.value, setFullName)
            }
            placeholder="Enter your full name"
            error={fieldErrors.fullName}
            required
          />

          {/* Email & Role */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              label="Email address"
              type="email"
              placeholder="Enter your email address"
              value={user.email}
              disabled
            />
            <TextInput
              label="Role"
              placeholder="Enter your role"
              value={user.role}
              disabled
              sx={{ textTransform: "capitalize" }}
            />
          </div>

          {/* Mobile */}
          <TextInput
            label="Mobile Number"
            value={mobile}
            inputMode="numeric"
            placeholder="Enter Mobile Number"
            maxLength={10}
            onChange={(e) =>
              setAndValidate(
                "mobile",
                normalizeMobile(e.target.value),
                setMobile,
              )
            }
            error={fieldErrors.mobile}
            required
          />

          {/* PAN */}
          <TextInput
            label="PAN Number"
            value={pan}
            maxLength={10}
            placeholder="Enter PAN Number"
            onChange={(e) =>
              setAndValidate("pan", normalizePan(e.target.value), setPan)
            }
            error={fieldErrors.pan}
            required
          />

          {/* Trading Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Dropdown
              label="Trading Experience"
              value={experience}
              onChange={(e) =>
                setAndValidate("experience", e.target.value, setExperience)
              }
              options={[
                { value: "Beginner", label: "Beginner" },
                { value: "Intermediate", label: "Intermediate" },
                { value: "Expert", label: "Expert" },
              ]}
              error={fieldErrors.experience}
              required
            />

            <Dropdown
              label="Risk Appetite"
              value={risk}
              onChange={(e) => setAndValidate("risk", e.target.value, setRisk)}
              options={[
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" },
              ]}
              error={fieldErrors.risk}
              required
            />
          </div>

          {/* Joined Date */}
          {joinedAt && (
            <div>
              <p className="text-xs text-gray-500">
                Member since <span className="font-medium">{joinedAt}</span>
              </p>
            </div>
          )}

          {/* Message */}
          {message && (
            <div className="text-sm text-primary-700 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
              {message}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving || !isChanged}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
